'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { User, Target, Palette, CheckCircle, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'

// Definição das fases do formulário
const PHASES = [
  { name: 'Contato', steps: [0, 1, 2], icon: User, color: 'violet' },
  { name: 'Projeto', steps: [3, 4, 5], icon: Target, color: 'blue' },
  { name: 'Detalhes', steps: [6, 7, 8], icon: Palette, color: 'fuchsia' },
  { name: 'Finalizar', steps: [9, 10, 11], icon: CheckCircle, color: 'green' }
]

interface EnhancedProgressProps {
  currentStep: number
  className?: string
}

export function EnhancedProgress({ currentStep, className }: EnhancedProgressProps) {
  // Encontrar fase atual
  const currentPhaseIndex = PHASES.findIndex(phase =>
    phase.steps.includes(currentStep)
  )
  const currentPhase = PHASES[currentPhaseIndex] || PHASES[0]

  // Calcular progresso total (0-100)
  const totalSteps = 12 // Steps 0-11
  const progress = Math.min(100, Math.round((currentStep / totalSteps) * 100))

  // Calcular progresso dentro da fase
  const stepsInPhase = currentPhase.steps.length
  const currentStepInPhase = currentPhase.steps.indexOf(currentStep)
  const phaseProgress = currentStepInPhase >= 0
    ? Math.round(((currentStepInPhase + 1) / stepsInPhase) * 100)
    : 0

  return (
    <div className={cn('w-full', className)}>
      {/* Indicadores de Fase */}
      <div className="flex items-center justify-between mb-3">
        {PHASES.map((phase, index) => {
          const PhaseIcon = phase.icon
          const isActive = index === currentPhaseIndex
          const isCompleted = index < currentPhaseIndex
          const isFuture = index > currentPhaseIndex

          return (
            <div key={phase.name} className="flex items-center">
              {/* Fase */}
              <motion.div
                className={cn(
                  'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all',
                  isActive && 'bg-violet-500/20 text-violet-500 dark:text-violet-400',
                  isCompleted && 'bg-green-500/20 text-green-500 dark:text-green-400',
                  isFuture && 'bg-indigo-950/5 dark:bg-white/5 text-indigo-950/50 dark:text-white/50'
                )}
                animate={isActive ? { scale: [1, 1.05, 1] } : {}}
                transition={{ duration: 0.5, repeat: isActive ? Infinity : 0, repeatDelay: 2 }}
              >
                <PhaseIcon size={12} />
                <span className="hidden sm:inline">{phase.name}</span>
                {isCompleted && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center"
                  >
                    <CheckCircle size={8} className="text-white" />
                  </motion.div>
                )}
              </motion.div>

              {/* Linha conectora */}
              {index < PHASES.length - 1 && (
                <div className="w-4 sm:w-8 h-0.5 mx-1 bg-indigo-950/10 dark:bg-white/10 relative overflow-hidden">
                  <motion.div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-violet-500 to-fuchsia-500"
                    initial={{ width: '0%' }}
                    animate={{
                      width: isCompleted ? '100%' : isActive ? `${phaseProgress}%` : '0%'
                    }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Barra de Progresso Principal */}
      <div className="relative h-2 bg-indigo-950/10 dark:bg-white/10 rounded-full overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-950/10 via-indigo-950/10 to-indigo-950/10 dark:from-white/10 dark:via-white/10 dark:to-white/10 opacity-50" />

        {/* Progress fill */}
        <motion.div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-violet-500 rounded-full"
          initial={{ width: '0%' }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          {/* Shimmer effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 dark:via-white/30 to-transparent"
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          />
        </motion.div>

        {/* Glow at the end */}
        <motion.div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-fuchsia-400 rounded-full blur-md"
          animate={{ left: `calc(${progress}% - 8px)` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      {/* Info da Fase Atual */}
      <div className="flex items-center justify-between mt-2 text-xs">
        <div className="flex items-center gap-1.5 text-indigo-950/60 dark:text-white/60">
          <Sparkles size={12} className="text-violet-500 dark:text-violet-400" />
          <span>
            {currentPhase.name} - Etapa {currentStepInPhase + 1}/{stepsInPhase}
          </span>
        </div>
        <motion.span
          className="text-violet-500 dark:text-violet-400 font-medium"
          key={progress}
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          {progress}%
        </motion.span>
      </div>
    </div>
  )
}

// Componente de celebração quando muda de fase
interface PhaseCelebrationProps {
  phaseName: string
  show: boolean
  onComplete: () => void
}

export function PhaseCelebration({ phaseName, show, onComplete }: PhaseCelebrationProps) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: -20 }}
          onAnimationComplete={() => {
            setTimeout(onComplete, 1500)
          }}
          className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none"
        >
          <div className="bg-white dark:bg-black border border-purple-500/20 dark:border-white/10 rounded-2xl px-8 py-6 shadow-2xl shadow-purple-500/20">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: [0, 1.2, 1] }}
              transition={{ duration: 0.5 }}
              className="flex items-center justify-center mb-3"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-full flex items-center justify-center">
                <CheckCircle size={32} className="text-white" />
              </div>
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg font-semibold text-indigo-950 dark:text-white text-center"
            >
              Fase "{phaseName}" Concluída!
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-sm text-indigo-950/60 dark:text-white/60 text-center mt-1"
            >
              Continue assim!
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
